/**
 * Renders one CREATIVE and, inline beneath it, the ASSETs it owns. The parent
 * concept fetches every creative's assets in one batch and hands each creative
 * its share, so this block issues no query of its own. All assets are shown
 * regardless of status; removed (retired) ones stay visible but de-emphasized so
 * the creative's full history reads at a glance without competing with live media.
 */
import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { PublisherEntityBadge, derivePublisherEntityStatus } from 'src/utils/publisherEntityStatus';
import { EntityAssetCard } from './EntityAssetCard';
import { EntityDetailsToggle } from './EntityDetailsToggle';
import { EntityIcon } from './EntityIcon';
import { readMetadataName } from './adspEntityHelpers';
import { EMPTY_VALUE, StatusBadge } from './StatusBadge';

export const REMOVED_ASSET_OPACITY = 0.5;

interface AdspCreativeBlockProps {
  assets: readonly PublisherEntityDto[];
  creative: PublisherEntityDto;
}

function isRemoved (entity: PublisherEntityDto): boolean {
  return derivePublisherEntityStatus(entity) === PublisherEntityBadge.REMOVED;
}

export const AdspCreativeBlock: React.FC<AdspCreativeBlockProps> = ({ assets, creative }) => {
  const { t } = useTranslation();
  const name = readMetadataName(creative) ?? creative.sourceEntityId ?? EMPTY_VALUE;

  return (
    <Box data-testid={`ads-manager-creative-${creative._id}`} spacingInset="300" type="outline">
      <Column spacing="200">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Row alignmentVertical="center" spacing="200">
            <EntityIcon size={24} type={creative.publisherEntityType} />
            <Text type="b200">{name}</Text>
          </Row>
          <StatusBadge entity={creative} />
        </Row>
        {creative.lastError != null && (
          <Text color="error" type="b100">{creative.lastError}</Text>
        )}
        <EntityDetailsToggle entity={creative} testId={`ads-manager-creative-${creative._id}`} />

        {assets.length === 0
          ? (
            <Text
              color="secondary"
              data-testid={`ads-manager-creative-no-assets-${creative._id}`}
              type="b100"
            >
              {t('ads_manager_no_creative_assets')}
            </Text>
            )
          : (
            <Column data-testid={`ads-manager-creative-assets-${creative._id}`} spacing="200">
              <Text color="secondary" type="b100">
                {t('ads_manager_ad_details_assets')} ({assets.length})
              </Text>
              <div style={{ maxWidth: 'calc(100vw - 440px)', overflowX: 'auto', overflowY: 'hidden', paddingBottom: 8, WebkitOverflowScrolling: 'touch' }}>
                <div style={{ display: 'inline-flex', gap: 12 }}>
                  {assets.map(asset => (
                    <div
                      data-testid={`ads-manager-creative-asset-${asset._id}`}
                      key={asset._id}
                      style={isRemoved(asset) ? { opacity: REMOVED_ASSET_OPACITY } : undefined}
                    >
                      <EntityAssetCard entity={asset} />
                    </div>
                  ))}
                </div>
              </div>
            </Column>
            )}
      </Column>
    </Box>
  );
};
